"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelFieldSubFormInstance = exports.CollectionModelBase = void 0;
const collection_field_1 = require("./collection.field");
const field_factory_1 = require("./fields/field.factory");
const form_field_1 = require("./fields/form.field");
const location_field_1 = require("./fields/location.field");
const true_size_field_1 = require("./fields/true.size.field");
class CollectionModelBase {
    get instanceId() {
        return this._instanceId;
    }
    set instanceId(value) {
        this._instanceId = value;
    }
    constructor(parent, id, schema) {
        this.fields = {};
        this.parent = parent;
        this.schema = schema;
        if (id != null) {
            this.instanceId = id;
        }
    }
    getField(fieldId) {
        let field = this.fields[fieldId];
        if (field == null) {
            const fieldSchema = this.schema.getField(fieldId);
            if (fieldSchema == null) {
                throw new Error(`Unable to find field ${this.instanceId}.${fieldId}`);
            }
            field = field_factory_1.FieldFactory.create(this, fieldSchema);
            this.fields[fieldId] = field;
        }
        return field;
    }
    getOptionalField(fieldId) {
        let field = this.fields[fieldId];
        if (field == null) {
            if (!this.schema.hasField(fieldId)) {
                return null;
            }
            const fieldSchema = this.schema.getField(fieldId);
            field = field_factory_1.FieldFactory.create(this, fieldSchema);
            this.fields[fieldId] = field;
        }
        return field;
    }
    getFields() {
        return this.schema.fields.map((field) => this.getField(field.id));
    }
    getFieldDeep(fieldId) {
        let result;
        this.forEachField((x) => {
            if (fieldId === x.id) {
                result = x;
            }
        });
        return result;
    }
    hasField(fieldId) {
        return (this.fields[fieldId] != null ||
            this.collection.state.hasFieldValue(collection_field_1.CollectionModelField.getStateId(this, fieldId)));
    }
    get title() {
        const titleField = this.schema.titleField;
        if (titleField == null) {
            return this.formTitle;
        }
        const titleCollectionField = this.getField(titleField.id);
        if (!titleCollectionField.hasValue() ||
            titleCollectionField.displayValue == null ||
            titleCollectionField.displayValue === '') {
            return this.formTitle;
        }
        return titleCollectionField.displayValue;
    }
    get titleField() {
        const titleField = this.schema.titleField;
        return titleField.id;
    }
    get subtitle() {
        const subtitleField = this.schema.subtitleField;
        if (subtitleField == null) {
            return null;
        }
        const subtitleCollectionField = this.getField(subtitleField.id);
        if (!subtitleCollectionField.hasValue() ||
            subtitleCollectionField.displayValue == null ||
            subtitleCollectionField.displayValue === '') {
            return null;
        }
        return subtitleCollectionField.displayValue;
    }
    get siteType() {
        const siteTypeField = this.schema.siteTypeField;
        if (siteTypeField == null) {
            return null;
        }
        const siteTypeCollectionField = this.getField(siteTypeField.id);
        if (!siteTypeCollectionField.hasValue() ||
            siteTypeCollectionField.displayValue == null ||
            siteTypeCollectionField.displayValue === '') {
            return null;
        }
        return siteTypeCollectionField.value;
    }
    get dropPole() {
        const dropPoleField = this.schema.dropPoleField;
        if (dropPoleField == null) {
            return null;
        }
        const dropPoleCollectionField = this.getField(dropPoleField.id);
        if (!dropPoleCollectionField.hasValue() ||
            dropPoleCollectionField.displayValue == null ||
            dropPoleCollectionField.displayValue === '') {
            return null;
        }
        return dropPoleCollectionField.value;
    }
    get poleLoading() {
        const poleLoadingField = this.schema.poleLoadingField;
        if (poleLoadingField == null) {
            return null;
        }
        const poleLoadingCollectionField = this.getField(poleLoadingField.id);
        if (!poleLoadingCollectionField.hasValue() ||
            poleLoadingCollectionField.displayValue == null ||
            poleLoadingCollectionField.displayValue === '') {
            return null;
        }
        return poleLoadingCollectionField.value;
    }
    /**
     * Return the first location found in the collection.
     * This is either the first location field or, if none exists, the first truesize image’s location is used.
     */
    get location() {
        const result = {
            locationValue: null,
            trueSizeValue: null,
        };
        this.forEachField((field) => {
            if (location_field_1.CollectionModelFieldLocation.isLocationField(field) && field.hasValue()) {
                result.locationValue = field.value;
                return false; // break the loop early
            }
            if (result.trueSizeValue == null && true_size_field_1.CollectionModelFieldTrueSize.isTrueSizeField(field)) {
                const captures = field.getCaptures();
                if (captures.length > 0) {
                    result.trueSizeValue = captures[0].target;
                }
            }
            return true;
        });
        return result.locationValue || result.trueSizeValue;
    }
    /**
     * Returns the location field found in the collection
     * This is either in the first location field or, if none exists, the first truesize image is used.
     */
    get locationModelField() {
        let result = {
            location: null,
            truesize: null,
        };
        this.forEachField((field) => {
            if (location_field_1.CollectionModelFieldLocation.isLocationField(field) && field.hasValue()) {
                result.location = field;
                return false;
            }
            return true;
        });
        if (!result.location) {
            this.forEachField((field) => {
                if (!result.truesize && true_size_field_1.CollectionModelFieldTrueSize.isTrueSizeField(field)) {
                    result.truesize = field;
                    return false;
                }
                return true;
            });
        }
        return result.location || result.truesize;
    }
    /**
     * Get the first location field (not value) in the form (not truesize)
     */
    get editableLocationField() {
        let locationField = null;
        this.forEachField((field) => {
            if (location_field_1.CollectionModelFieldLocation.isLocationField(field)) {
                locationField = field;
                return false; // break loop after location is found
            }
            return true;
        });
        return locationField;
    }
    /**
     * Iterate over all fields in the collection,
     * This will do a depth first traversal.
     *
     * Returning false at any stage will abort the traversal.
     *
     * @param func Function to call on every field.
     * @returns Whether all fields were processed
     */
    forEachField(func) {
        for (const schema of this.schema.fields) {
            const collectionField = this.getField(schema.id);
            const result = func(collectionField);
            if (result === false) {
                return false;
            }
            if (form_field_1.CollectionModelFieldSubForm.isSubFormField(collectionField)) {
                for (const instance of collectionField.value) {
                    const res = instance.forEachField(func);
                    if (res === false) {
                        return false;
                    }
                }
            }
        }
        return true;
    }
    get formTitle() {
        return null;
    }
}
exports.CollectionModelBase = CollectionModelBase;
class CollectionModelFieldSubFormInstance extends CollectionModelBase {
    get collection() {
        return this.parent.collection;
    }
    get formTitle() {
        const name = this.schema.displayName || '';
        const parent = this.parent;
        const index = parent.value.indexOf(this);
        return `${name} #${index + 1}`;
    }
}
exports.CollectionModelFieldSubFormInstance = CollectionModelFieldSubFormInstance;
//# sourceMappingURL=collection.base.js.map